import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useRef, useState } from 'react';
import jsCookie from "js-cookie";
import { useSearchParams } from 'react-router-dom';
import { verifyPaymentStatus } from './action';
import { API_PROD_URL } from "src/utils/axios";

const stripePromise = loadStripe(process.env.REACT_APP_REPORT_ANYTHING_STRIPE_PUBLISHABLE_KEY || '');

interface CheckoutFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  userId?: string;
  threadId?: string;
  onPaymentSuccess?: () => void;
  onModalClose?: () => void;
  selectedPlan?: 'one-time' | 'monthly' | null;
}

function CheckoutForm({ onSuccess, onError, userId, threadId, onPaymentSuccess, onModalClose, selectedPlan }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const paymentAmount = selectedPlan === 'monthly' ? '$2,700.00' : '$1,500.00';
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [countdown, setCountdown] = useState(3);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Confirm payment without redirect
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: 'if_required',
      });

      if (error) {
        // Check if payment already succeeded
        if (error.message?.includes('already succeeded')) {
          setErrorMessage('This payment has already been processed successfully.');

          // Still try to verify with backend if we have user/thread info
          if (userId && threadId) {
            setIsVerifying(true);
            setIsProcessing(false);
            try {
              const status = await verifyPaymentStatus(userId, threadId);
              if (status === 'success') {
                setPaymentSuccess(true);
                onPaymentSuccess?.(); // ✅ Call parent callback
                onSuccess?.();
                
                // Start countdown
                let count = 3;
                setCountdown(count);
                const interval = setInterval(() => {
                  count -= 1;
                  setCountdown(count);
                  if (count <= 0) {
                    clearInterval(interval);
                    onModalClose?.();
                  }
                }, 1000);
              }
            } catch (verifyError) {
              console.error('Verification error:', verifyError);
            } finally {
              setIsVerifying(false);
            }
          } else {
            setIsProcessing(false);
          }
        } else {
          setErrorMessage(error.message || 'An error occurred');
          onError?.(error.message || 'An error occurred');
          setIsProcessing(false);
        }
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Verify with backend before showing success
        setIsVerifying(true);
        setIsProcessing(false);

        if (userId && threadId) {
          try {
            const status = await verifyPaymentStatus(userId, threadId);
            if (status === 'success') {
              setPaymentSuccess(true);
              onPaymentSuccess?.(); // ✅ Call parent callback FIRST
              onSuccess?.();
              
              // Start countdown
              let count = 3;
              setCountdown(count);
              const interval = setInterval(() => {
                count -= 1;
                setCountdown(count);
                if (count <= 0) {
                  clearInterval(interval);
                  onModalClose?.();
                }
              }, 1000);
            } else {
              setErrorMessage('Payment verification failed. Please contact support.');
              onError?.('Payment verification failed');
            }
          } catch (verifyError) {
            setErrorMessage('Unable to verify payment. Please contact support.');
            onError?.('Verification error');
          } finally {
            setIsVerifying(false);
          }
        } else {
          // No userId/threadId, just show success
          setPaymentSuccess(true);
          onPaymentSuccess?.(); // ✅ Call parent callback
          onSuccess?.();
          
          // Start countdown
          let count = 3;
          setCountdown(count);
          const interval = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(interval);
              onModalClose?.();
            }
          }, 1000);
          
          setIsVerifying(false);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrorMessage(message);
      onError?.(message);
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="text-center py-6">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 p-3 rounded-full animate-bounce">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h3>
        <p className="text-base text-gray-600 mb-4">Your payment of {paymentAmount} has been processed.</p>
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Closing in {countdown} second{countdown !== 1 ? 's' : ''}...</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || isProcessing || isVerifying}
        className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
          !stripe || isProcessing || isVerifying
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-indigo-600 hover:bg-indigo-700'
        }`}
      >
        {isProcessing ? 'Processing Payment...' : isVerifying ? 'Verifying...' : 'Complete Payment'}
      </button>
    </form>
  );
}

interface StripeConnectProps {
  currency?: string;
  userId?: string;
  threadId?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  pipelineTriggerFn?: () => void;
  onPaymentSuccess?: () => void; 
}

export default function StripeConnect({
  currency = 'usd',
  onSuccess,
  onError,
  pipelineTriggerFn,
  onPaymentSuccess // ✅ Receive the callback
}: StripeConnectProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState<'one-time' | 'monthly' | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'one-time' | 'monthly' | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [searchParams] = useSearchParams();
  const userId = jsCookie.get("user_id");
  const threadId = searchParams.get("threadId");

  const isProcessingRef = useRef(false);

  const closeModal = () => {
    setClientSecret(null);
    setShowInfoModal(false);
    setError(null);
    setActivePlan(null);
    setSelectedPlan(null);
  };

  const createPaymentIntent = async (
    amountInCents: number,
    plan: 'one-time' | 'monthly'
  ) => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setActivePlan(plan);
    setError(null);
    pipelineTriggerFn?.();

    try {
      if (!userId || !threadId) {
        throw new Error('User ID and Thread ID are required for payment');
      }

      const response = await fetch(
        `${API_PROD_URL}/stripe/create-payment-intent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: amountInCents,
            currency,
            user_id: userId,
            thread_id: threadId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment intent');
      }

      const data = await response.json();
      setClientSecret(data.client_secret);
      setSelectedPlan(plan);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to initialize payment';
      setError(message);
      onError?.(message);
    } finally {
      isProcessingRef.current = false;
      setActivePlan(null);
    }
  };

 const oneTimeReport = () => createPaymentIntent(150000, 'one-time');
  // const oneTimeReport = () => createPaymentIntent(100, 'one-time');
  const monthlySubscription = () => createPaymentIntent(270000, 'monthly'); // $2,700 flat for 6 months

  if (!clientSecret && !showInfoModal) {
    return (
      <button
        onClick={() => setShowInfoModal(true)}
        className="flex items-center gap-2 px-2 py-1 rounded-xl text-base font-semibold transition-all bg-primary-900 hover:bg-primary-800 text-white shadow-lg hover:shadow-xl cursor-pointer"
      >
        <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        Purchase Full Report Now
      </button>
    );
  }

  if (!clientSecret && showInfoModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
          {/* Close button */}
          <button
            onClick={() => {
              setShowInfoModal(false);
              setError(null);
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Purple gradient top border */}
          {/* <div className="h-2 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-500 rounded-t-2xl" /> */}

          <div className="p-4 pt-6">
            {/* Lock Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-[#6B21A8] rounded-full flex items-center justify-center shadow-lg p-2">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">
              Unlock Full Market Analysis
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-center mb-3 leading-relaxed">
              Get complete access to 7 in-depth sections including competitive landscape, SWOT analysis, Porter's Five Forces, pricing sensitivity, and strategic recommendations.
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">60+ page executive report</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">Validated market data</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">Strategic roadmap</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <span className="text-gray-700 text-sm font-medium">Competitor analysis</span>
              </div>
            </div>

            {/* Pricing Card */}

            <div className='grid grid-cols-1 gap-2'>
              <div>
                <div className="flex flex-col justify-center bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-3 mb-2 border border-slate-200 h-[250px]">
                  <p className="text-center text-gray-900 text-lg mb-2">One-time Report</p>
                  <div className="text-center">
                    <span className="text-4xl font-bold text-[#6B21A8]">$1,500</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="text-gray-400 line-through text-xl">$3,000</span>
                    <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Save 50%</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={oneTimeReport}
                  disabled={activePlan !== null}
                  className={`w-full py-2 px-2 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    activePlan === 'one-time'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#6B21A8] hover:bg-[#581c87] text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {activePlan === 'one-time' ? 'Loading...' : 'Purchase One-time Report'}
                </button>

              </div>
            </div>


            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 mb-4">
                {error}
              </div>
            )}

            {/* Purchase Button */}
            

            {/* Footer */}
            <p className="text-center text-gray-500 text-sm mt-2">
              Delivered in 7 business days &bull; PowerPoint + PDF + Excel
            </p>
          </div>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret: clientSecret as string,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[90vh] flex flex-col">
          {/* Fixed Header */}
          <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-sm font-semibold text-gray-800">Complete Payment</h3>
              <span className="text-gray-400 line-through text-xs">
                {selectedPlan === 'monthly' ? '$5,400' : '$3,000'}
              </span>
              <span className="text-green-600 font-bold text-sm">
                {selectedPlan === 'monthly' ? '$2,700' : '$1,500'}
              </span>
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto p-4 flex-1">
            <CheckoutForm
              onSuccess={onSuccess}
              onError={onError}
              userId={userId}
              threadId={threadId || undefined}
              onPaymentSuccess={onPaymentSuccess}
              onModalClose={closeModal}
              selectedPlan={selectedPlan}
            />
          </div>
        </div>
      </div>
    </Elements>
  );
}
